import type { AnyGameType } from '../../shared/protocol.js';

/** Small illustrative icon per game module. */
export function GameIcon(props: { game: AnyGameType; size?: number }) {
  const size = props.size ?? 40;
  switch (props.game) {
    case 'battleship':
      return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
          <defs>
            <linearGradient id="gi-sea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#5cc8ff" />
              <stop offset="1" stop-color="#2d6cdf" />
            </linearGradient>
          </defs>
          <rect x="2" y="2" width="44" height="44" rx="12" fill="url(#gi-sea)" opacity="0.18" />
          {/* Top-down hull */}
          <path d="M24 7 C29 12 31 18 31 24 L31 36 C31 39 28 41 24 41 C20 41 17 39 17 36 L17 24 C17 18 19 12 24 7 Z" fill="#cfe6ff" />
          <rect x="21" y="17" width="6" height="8" rx="1.5" fill="#5b7aa6" />
          <circle cx="24" cy="30" r="2.6" fill="#5b7aa6" />
          <rect x="23.2" y="11" width="1.6" height="7" rx="0.8" fill="#5b7aa6" />
          <circle cx="24" cy="36" r="1.6" fill="#5b7aa6" />
        </svg>
      );
    case 'othello':
      return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
          <rect x="2" y="2" width="44" height="44" rx="12" fill="#1f7a4d" opacity="0.35" />
          <circle cx="17" cy="17" r="8" fill="#f1f5f9" />
          <circle cx="31" cy="17" r="8" fill="#0b1220" stroke="#64748b" stroke-width="1.5" />
          <circle cx="17" cy="31" r="8" fill="#0b1220" stroke="#64748b" stroke-width="1.5" />
          <circle cx="31" cy="31" r="8" fill="#f1f5f9" />
        </svg>
      );
    case 'memory':
      return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
          <rect x="2" y="2" width="44" height="44" rx="12" fill="#7c3aed" opacity="0.25" />
          <rect x="8" y="10" width="15" height="21" rx="3" fill="#6d28d9" transform="rotate(-8 15 20)" />
          <rect x="24" y="14" width="15" height="21" rx="3" fill="#f8fafc" transform="rotate(8 31 24)" />
          <path d="M31.5 19.5l2 4 4.4.6-3.2 3.1.8 4.4-4-2.1-4 2.1.8-4.4-3.2-3.1 4.4-.6z" fill="#facc15" transform="rotate(8 31 24)" />
        </svg>
      );
    case 'chess':
      return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
          <rect x="2" y="2" width="44" height="44" rx="12" fill="#c69a6b" opacity="0.25" />
          {/* Checkered corner and a knight silhouette */}
          <g opacity="0.45">
            <rect x="6" y="30" width="6" height="6" fill="#f0d9b5" />
            <rect x="12" y="36" width="6" height="6" fill="#f0d9b5" />
            <rect x="6" y="36" width="6" height="6" fill="#b58863" />
            <rect x="12" y="30" width="6" height="6" fill="#b58863" />
          </g>
          <path
            d="M20 40h18v-3.5c0-1-.6-1.6-1.6-1.8-.9-4-1.4-8.7-.6-12.8.9-4.6-2-9.8-7.6-11.4l-1.2-3-2.3 3.1c-3.6 1.1-6.7 4.6-8.5 8.3l-2.5 4.4c-.6 1.1.2 2.4 1.4 2.4h2.2c1.4 0 2.6-.7 3.3-1.9l1.5-.7c.4 2.7-1.7 5.6-4.1 8.3-.9 1-1.3 2.2-1.3 3.5V40z"
            fill="#f8fafc"
            stroke="#1e293b"
            stroke-width="1.6"
            stroke-linejoin="round"
          />
          <circle cx="25.5" cy="16.5" r="1.3" fill="#1e293b" />
        </svg>
      );
    case 'snake':
      return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
          <rect x="2" y="2" width="44" height="44" rx="12" fill="#84cc16" opacity="0.22" />
          {/* Pixel snake on a grid, like the old phones */}
          <g fill="#65a30d">
            <rect x="8" y="30" width="6" height="6" rx="1" />
            <rect x="14" y="30" width="6" height="6" rx="1" />
            <rect x="20" y="30" width="6" height="6" rx="1" />
            <rect x="20" y="24" width="6" height="6" rx="1" />
            <rect x="20" y="18" width="6" height="6" rx="1" />
            <rect x="26" y="18" width="6" height="6" rx="1" />
          </g>
          <rect x="32" y="18" width="6" height="6" rx="1" fill="#a3e635" />
          <rect x="35" y="19.5" width="1.6" height="1.6" fill="#1a2e05" />
          <circle cx="35" cy="34" r="3.2" fill="#ef4444" />
          <rect x="34.4" y="29" width="1.2" height="2.6" rx="0.6" fill="#65a30d" />
        </svg>
      );
    case 'tetrax':
      return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
          <rect x="2" y="2" width="44" height="44" rx="12" fill="#a855f7" opacity="0.2" />
          {/* Falling blocks: a T coming down onto a stack with a gap */}
          <g stroke="#0f172a" stroke-width="0.8">
            <rect x="15" y="8" width="6" height="6" fill="#a855f7" />
            <rect x="21" y="8" width="6" height="6" fill="#a855f7" />
            <rect x="27" y="8" width="6" height="6" fill="#a855f7" />
            <rect x="21" y="14" width="6" height="6" fill="#a855f7" />
            <rect x="9" y="28" width="6" height="6" fill="#22d3ee" />
            <rect x="9" y="34" width="6" height="6" fill="#22d3ee" />
            <rect x="15" y="34" width="6" height="6" fill="#facc15" />
            <rect x="27" y="34" width="6" height="6" fill="#22c55e" />
            <rect x="33" y="34" width="6" height="6" fill="#22c55e" />
            <rect x="33" y="28" width="6" height="6" fill="#f97316" />
          </g>
        </svg>
      );
    case 'vale':
      return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
          <rect x="2" y="2" width="44" height="44" rx="12" fill="#e5484d" opacity="0.2" />
          {/* A face-down card behind, a face-up seven and a "?" of the bluff */}
          <rect x="9" y="11" width="18" height="26" rx="3" fill="#3b5bdb" stroke="#dbe4ff" stroke-width="1.5" transform="rotate(-14 18 24)" />
          <rect x="19" y="10" width="18" height="26" rx="3" fill="#f8fafc" stroke="#94a3b8" stroke-width="1" transform="rotate(10 28 23)" />
          <text x="28" y="27" font-size="11" font-weight="800" fill="#e5484d" text-anchor="middle" transform="rotate(10 28 23)" font-family="system-ui, sans-serif">
            7♥
          </text>
          <circle cx="36" cy="36" r="7" fill="#facc15" />
          <text x="36" y="40" font-size="10" font-weight="900" fill="#1e293b" text-anchor="middle" font-family="system-ui, sans-serif">
            ?
          </text>
        </svg>
      );
    case 'dice':
      return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
          <rect x="2" y="2" width="44" height="44" rx="12" fill="#f59e0b" opacity="0.22" />
          <rect x="7" y="15" width="19" height="19" rx="4" fill="#f8fafc" transform="rotate(-12 16.5 24.5)" />
          <g fill="#0b1220" transform="rotate(-12 16.5 24.5)">
            <circle cx="12" cy="20" r="1.8" />
            <circle cx="16.5" cy="24.5" r="1.8" />
            <circle cx="21" cy="29" r="1.8" />
          </g>
          <rect x="23" y="10" width="18" height="18" rx="4" fill="#e5484d" transform="rotate(14 32 19)" />
          <g fill="#fff" transform="rotate(14 32 19)">
            <circle cx="27.5" cy="14.5" r="1.7" />
            <circle cx="36.5" cy="14.5" r="1.7" />
            <circle cx="27.5" cy="23.5" r="1.7" />
            <circle cx="36.5" cy="23.5" r="1.7" />
          </g>
        </svg>
      );
  }
}
